import { useLayoutEffect, useRef, useState } from "react";
import { motion, useMotionValueEvent, useReducedMotion, useScroll, useSpring, useTransform, type MotionValue } from "motion/react";
import * as Flags from "country-flag-icons/react/3x2";

const TEAL = "#2EE6D6";

/*
 * A line of flags between sections, not a section of its own. Flags of the
 * countries our students come from roll right as you scroll down, into
 * Austria near the right edge. Each one rolls and shrinks into the Austrian
 * flag as it arrives, and Austria pulses. Scrolling up
 * sends them back out. The row fades in from the left, so the supply of
 * countries never looks finished. Decorative only; reduced motion holds it
 * still.
 */
/*
 * Order matters: it is what people see side by side. Regions and colours
 * alternate (South Asia, Latin America, East Asia, Africa, Europe, the Middle
 * East), and flags with a crescent (TR, PK, UZ, AZ, TN) stay at least five
 * places apart, so no stretch of the row reads as one region or one symbol.
 */
const COUNTRIES = [
  "IN", "BR", "CN", "NG", "TR", "KE", "MX", "BD", "UA", "GH", "VN", "IR", "CO", "KZ", "PH", "PK",
  "ET", "AR", "ID", "GE", "EG", "UZ", "PE", "RS", "LK", "CM", "AZ", "JO", "MA", "AL", "KG", "TN",
];

function Flag({ code, size }: { code: string; size: number }) {
  const F = Flags[code as keyof typeof Flags] as React.ComponentType<{ className?: string }> | undefined;
  return (
    <span className="relative block overflow-hidden rounded-full shadow-[0_0_0_1px_rgba(10,15,14,0.08),0_6px_14px_rgba(10,15,14,0.12)]" style={{ width: size, height: size }}>
      {F && <F className="absolute left-1/2 top-0 h-full w-auto max-w-none -translate-x-1/2" />}
    </span>
  );
}

/** One flag on the line. `base` is its x when the line has not moved yet. */
function Traveller({ code, base, shift, dest, size }: { code: string; base: number; shift: MotionValue<number>; dest: number; size: number }) {
  const x = useTransform(shift, (s) => Math.min(base + s, dest) - size / 2);
  // Rolls like a wheel, at half a true wheel's turn so it stays calm to watch.
  const rotate = useTransform(shift, (s) => ((Math.min(base + s, dest) - base) / (Math.PI * size)) * 180);
  // In the last stretch before Austria, the flag shrinks into it.
  const near = useTransform(shift, (s) => Math.min(1, Math.max(0, (base + s - (dest - size * 1.1)) / (size * 1.1))));
  const scale = useTransform(near, (n) => 1 - n * 0.75);
  const opacity = useTransform(near, (n) => (n >= 1 ? 0 : 1 - n * 0.6));
  return (
    <motion.span className="absolute left-0 top-1/2" style={{ x, y: "-50%", rotate, scale, opacity }}>
      <Flag code={code} size={size} />
    </motion.span>
  );
}

export function FlagRoll() {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion() ?? false;
  const [width, setWidth] = useState(1200);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const phone = width < 640;
  const size = phone ? 52 : 88;
  const gap = phone ? 12 : 24;
  const step = size + gap;
  const austria = phone ? 72 : 118;
  const dest = width * (phone ? 0.8 : 0.76);

  // The whole time the line is on screen maps to about five flags arriving,
  // so the pace follows the hand. A light spring keeps fast flicks smooth.
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const raw = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : step * 5]);
  const shift = useSpring(raw, { stiffness: 120, damping: 26, mass: 0.6 });

  // Austria pulses each time another flag arrives.
  const [arrived, setArrived] = useState(0);
  useMotionValueEvent(shift, "change", (s) => setArrived(Math.max(0, Math.floor((s + step * 0.1) / step))));

  return (
    <div className="overflow-hidden bg-white pb-4 sm:pb-8" aria-hidden="true">
      <div
        ref={ref}
        className="relative h-[112px] sm:h-[172px]"
        style={{
          WebkitMaskImage: "linear-gradient(90deg, transparent 0%, #000 18%, #000 100%)",
          maskImage: "linear-gradient(90deg, transparent 0%, #000 18%, #000 100%)",
        }}
      >
        {COUNTRIES.map((c, i) => (
          <Traveller key={c} code={c} base={dest - (i + 1) * step} shift={shift} dest={dest} size={size} />
        ))}

        {/* Austria, where the line leads. */}
        <div className="absolute top-1/2" style={{ left: dest, transform: "translate(-50%, -50%)" }}>
          <motion.span
            key={arrived}
            className="absolute inset-0 rounded-full"
            style={{ backgroundColor: TEAL }}
            initial={{ scale: 1, opacity: reduced ? 0 : 0.55 }}
            animate={{ scale: 1.45, opacity: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
          />
          <span className="relative block overflow-hidden rounded-full shadow-[0_0_0_4px_#FFFFFF,0_0_0_7px_rgba(46,230,214,0.45),0_12px_26px_rgba(10,15,14,0.18)]" style={{ width: austria, height: austria }}>
            <Flags.AT className="absolute left-1/2 top-0 h-full w-auto max-w-none -translate-x-1/2" />
          </span>
        </div>
      </div>
    </div>
  );
}
